import { useCallback, useMemo, useState } from "react";
import {
  isFidoPilotAccount,
  resolveTwoFactorAccountKey,
} from "../Constants/webauthnMock";
import { useAuth } from "../Contexts/AuthContext";
import { useTwoFactorSettings } from "./useTwoFactorSettings";

type AccountData = {
  userId?: string;
  userDocument?: string;
  userDocumentRaw?: string;
};

const resolvePilotStatus = (
  accountData: AccountData,
  authUsername?: string | null
): boolean => {
  const candidates = [
    accountData.userDocumentRaw,
    accountData.userDocument,
    authUsername,
    resolveTwoFactorAccountKey({
      username: authUsername,
      userDocument: accountData.userDocument,
      userDocumentRaw: accountData.userDocumentRaw,
      userId: accountData.userId,
    }),
  ].filter(Boolean);

  return candidates.some((candidate) => isFidoPilotAccount(candidate));
};

export const useFidoPilotOnboarding = (accountData: AccountData = {}) => {
  const { user } = useAuth();

  // 2FA / USB keys are per authenticated person — never the selected wallet account.
  const accountKey = useMemo(
    () =>
      resolveTwoFactorAccountKey({
        username: user?.username,
        userId: accountData.userId,
      }),
    [accountData.userId, user?.username]
  );

  const isPilot = useMemo(
    () => resolvePilotStatus(accountData, user?.username),
    [
      accountData.userDocument,
      accountData.userDocumentRaw,
      accountData.userId,
      user?.username,
    ]
  );

  const twoFactor = useTwoFactorSettings(accountKey);

  const [modalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const selectedLoginMethods = twoFactor.settings.selectedLoginMethods || [];
  const showBanner = selectedLoginMethods.length === 1;

  return {
    isPilot,
    accountKey,
    showBanner,
    modalOpen,
    openModal,
    closeModal,
    ...twoFactor,
  };
};
